import { cva } from 'class-variance-authority';
import { PHONE_INPUT_SIZE } from 'components/styles/phoneInputSize';

/**
 * Text input class recipes.
 *
 * Like `Button.recipe`, these contain ZERO `dark:` variants — colour comes entirely from slots set
 * per theme in `components/styles/design-system.css`. Each shape reads its OWN family: the 60px
 * recipes read `--ds-input-*`, which `.ds-input` writes, and the pill recipes read
 * `--ds-input-pill-*`, which `.ds-input-pill` writes. Neither shape can move the other's values.
 *
 * Every state selector reads Base UI's `Field.Root` attributes through the `group/ti` marker
 * (`data-focused`, `data-filled`, `data-invalid`, `data-disabled`), so there is no React state
 * behind the shrink or the colour changes — which also means the states are reachable from a
 * story or a test by rendering, not by simulating.
 *
 * The state selectors are written to be MUTUALLY EXCLUSIVE rather than relying on utility sort
 * order, the same discipline the button recipe uses. Precedence, highest first:
 *
 *   disabled → invalid → open → focused → hover → resting
 *
 * An errored field therefore stays red while focused, which is what the design's separate Error
 * and Focus cells imply.
 *
 * `open` is `Select`'s engaged state and no text input ever enters it — `Select` writes `data-open`
 * on `Field.Root` while its list is showing. Two things about where it sits are read off the file
 * rather than chosen:
 *
 *   It outranks FOCUS, because every open select is also focused — Base UI keeps the trigger focused
 *   for as long as the list is up — and the `Select` set draws the open cell for that pair, not the
 *   focused one. Without the rank the two fight on every open.
 *
 *   It yields BOTH the border and the fill to invalid, which is a MEASURED DIVERGENCE from the file
 *   rather than a reading of it. The set's eight `isOpen=Yes, hasError=Yes` cells do bind
 *   `background/control/active` alongside `border/destructive/default`, so composing the two is
 *   what the cells draw — but this field renders its helper text INSIDE the border, and the error
 *   helper measures **4.26:1** against the engaged fill in dark mode, under the 4.5:1 AA floor for
 *   the one string that tells the user what went wrong. The engaged fill is `neutral/800` in dark,
 *   four steps lighter than the resting `neutral/875` the red was picked against.
 *
 *   So an errored select keeps its resting fill while open. Nothing is lost from the state: the red
 *   border still says errored and the open panel still says engaged. The same class of divergence
 *   `--ds-input-border-caret` and `-border-invalid` already carry, and recorded as a library gap
 *   rather than settled here — if the library re-tunes either the engaged fill or the destructive
 *   helper role, this fence is the line to revisit.
 *
 * ── Where the padding lives ────────────────────────────────────────────────────────────────────
 *
 * All of it is on the CONTROL, none on the box. That is the one structural decision to understand
 * before changing anything here.
 *
 * The obvious build — pad the box, put a 20px-tall input on the value line — leaves the `<input>`
 * a thin strip across the middle of a 60px field, so most of what looks like a text box is dead to
 * the pointer and clicking near the edges does nothing. Paying the padding from inside the input
 * instead makes the element fill the whole box, so every pixel of the field is a click target and
 * the caret lands where it should. It is what MUI's outlined input does, for the same reason.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-input` slot class lives HERE rather than on the bordered box, even though the box is what
 * consumes most of the slots. Custom properties cascade, so everything inside inherits them — and
 * anything rendered as a SIBLING of the box (the pill's helper text) would otherwise fall outside
 * their scope and silently resolve to inherited colour.
 */
export const textInputRootRecipe = cva(['group/ti ds-input', 'relative flex w-full flex-col']);

/**
 * The bordered box.
 *
 * Holds no padding of its own (see the note above) — it is a border, a fill and a radius. The only
 * exception is the bottom inset when helper text is present, which the control can no longer pay for
 * because the helper sits below it.
 *
 * `gap-[4px]` separates the value block from the helper text. Absolutely positioned children (the
 * label and the icons) are out of flow, so the gap only ever applies between the control and the
 * helper row — a field without helper text gets no stray space.
 *
 * `min-h-[60px]` matches the design's resting height, which the control's own box already produces;
 * it is a floor rather than the mechanism, so a taller caller-supplied line height cannot clip.
 *
 * ── The state matrix, and which browser state each Figma column is ────────────────────────────
 *
 * The reworked set (447:35126) draws six state columns per Position row:
 *
 *   Default    background/control/default   border/control/default    1px
 *   Hover      background/control/hover     border/control/hover      1px
 *   Active     background/control/default   border/accent/default     2px
 *   Focus      background/control/default   border/control/default    1px  + the `Focused` effect
 *   Error      background/control/default   border/destructive        1px
 *   Disabled   background/control/disabled  border/control/disabled   1px
 *
 * `Focus` and `Active` are NOT two readings of the same thing, and an earlier revision of this file
 * merged them on the assumption that they were. They are two independent layers:
 *
 *   **`Active` is "this field holds the caret."** Any focus produces it — pointer or keyboard. It is
 *   the column that draws the caret, and it is `data-focused`.
 *
 *   **`Focus` is the ACCESSIBILITY ring, and nothing else.** It is the treatment a control gets when
 *   the user arrives by KEYBOARD. It is additive rather than alternative: it does not replace
 *   Active's border, it would add the halo outside it.
 *
 * Read that way the two compose rather than compete, and Figma's `Focus` cell stops looking
 * impossible: it draws a resting border and no caret, which a real text input can never show, but as
 * a LAYER shown on its own that is just "here is the ring".
 *
 * The catch is that the keyboard-only half is not expressible in CSS on a text input, so it is not
 * drawn at all today — see the halo note further down, which is the one open question in this file.
 *
 * Two inconsistencies in the `Active` column, neither reproduced: the halo appears on ONE of its
 * eight variants (Standalone, isFilled=No) — consistent with the halo being a separate layer that
 * happened to be left on in one cell — and three of the four Position rows fill it with
 * `background/control/hover` while Standalone fills with `default`, so the resting fill is kept.
 *
 * `Select` shares these recipes and has no `Active` cell at all, so it inherits this reading; raised
 * for design.
 *
 * ── Why the accent border is painted as a shadow rather than a wider border ────────────────────
 *
 * `border-width` NEVER changes here, in any state. `Active` is 2px where every other cell is 1px,
 * and the obvious build is to grow the border — but a border is layout. Widening it moves the
 * padding box, which shifts the absolutely positioned label and icons AND the value text by a
 * pixel, so the field's contents visibly jump on every focus and every tab through a group.
 *
 * So the border stays 1px and the second pixel is painted as an inset `box-shadow`, which occupies
 * no layout space:
 *
 *   inset 0 0 0 1px  --ds-input-stroke-extra   the pixel that makes the focused stroke 2px
 *
 * It defaults to `transparent`, so the declaration is unconditional and only the slot switches. It
 * is the only cell that needs one now: the previous generation authored error and disabled at 1.5px
 * too, and the reworked set draws both at 1px, so those two rules are deleted rather than retuned.
 *
 * ── The focus halo is NOT DRAWN, and this is the open question in this file ────────────────────
 *
 * `Focus` asks for a keyboard-only ring. **CSS cannot express that on a text input**, and the
 * measurement is unambiguous rather than a matter of opinion:
 *
 *   an `<input>` matches `:focus-visible` on a PLAIN MOUSE CLICK.
 *
 * That is deliberate UA behaviour, not a bug and not Chrome-specific — the spec has UAs match
 * `:focus-visible` whenever the focused element takes text input, on the reasoning that the user is
 * about to type and wants to see where. So `has-[input:focus-visible]`, which is what this family's
 * neighbours use, resolves to "any focus" here and the halo would appear on every click.
 *
 * Which is exactly the reported defect: a white 2px gap and a dark 4px line outside the blue border
 * on a mouse click reads as a SECOND BORDER around the field, and pushes 4px of shadow over the
 * neighbours inside a `FieldGroup`.
 *
 * So the halo is off, and the caret state's accent border is the whole focus treatment. That is safe
 * rather than a compromise: `border/accent` measures 9.11:1 light and 5.12:1 dark against the page,
 * and bottoms out at 4.70:1 against the dark control fill. At 2px it satisfies 1.4.11 and 2.4.11 on
 * its own. Nothing accessible is lost; what is lost is the keyboard-only DISTINCTION.
 *
 * `--ds-input-halo-inner` / `-outer` stay declared in `design-system.css`. They are not dead: they
 * are what a keyboard-only ring will paint with the moment the modality is knowable, and deleting
 * them would throw away a matched, measured pair.
 *
 * **What it would take.** `:focus-visible` is the wrong instrument; the right one is input-modality
 * tracking — a document-level listener flipping a flag on `keydown` vs `pointerdown`, which is what
 * react-aria does globally. That is a small hook, but it is SHARED INFRASTRUCTURE: `DatePicker`,
 * `TextArea` and `Select` all want the same answer, and giving it to one field alone would make a
 * mixed `FieldGroup` answer a Tab two different ways — the precise failure `DatePicker.recipe`
 * says the field primitives must avoid. So it is a family-wide follow-up, not a line in this file.
 *
 * `DatePicker` now draws the library's shared ring as part of its own reviewed component treatment.
 * That does not change this plain text-control decision: adding the ring here would make every mouse
 * click draw both an accent edge and a halo without adding an accessibility guarantee the edge does
 * not already provide. `TextArea.recipe` makes the same call.
 */
export const textInputBoxRecipe = cva(
  [
    'relative flex w-full flex-col gap-[4px]',
    'min-h-[60px]',
    'border-solid border-[length:var(--ds-input-border-width)] border-[var(--ds-input-border)]',
    'bg-[var(--ds-input-bg)]',
    'cursor-text',
    'transition-[border-color,background-color,box-shadow] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    /**
     * Hover is excluded from every other state so it can never fight them. It now moves the FILL as
     * well as the border, which is new in the reworked set — see `--ds-input-bg-hover`.
     *
     * All FOUR fences matter, and `:not([data-open])` is not redundant with `:not([data-focused])`
     * even though every open select is also focused: the two attributes are written by different
     * code — Base UI's `Field` sets one, `Select` sets the other — so stating the precedence the
     * header documents is what keeps a future change to either from silently reopening the fight.
     */
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid]):not([data-open])]/ti:border-[var(--ds-input-border-hover)]',
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid]):not([data-open])]/ti:bg-[var(--ds-input-bg-hover)]',

    /**
     * The ENGAGED state, which only `Select` sets — see the precedence note in the header.
     *
     * BOTH are fenced against `data-invalid`, and the fill's fence is the one to read the header
     * before changing: the open-and-errored cells do draw the engaged fill, and reproducing them
     * put the error helper at 4.26:1 in dark. Both are fenced against `data-disabled` too, and the
     * focus rule below is fenced against this one so no pair resolves by Tailwind's emission order.
     */
    'group-[[data-open]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-input-border-active)]',
    'group-[[data-open]:not([data-disabled]):not([data-invalid])]/ti:bg-[var(--ds-input-bg-active)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid]):not([data-open])]/ti:border-[var(--ds-input-border-caret)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-input-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-input-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-input-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Off by default, so the declaration below can be unconditional. One layer now that the halo
    // is not drawn — see the long note above, and keep the slot rather than inlining the colour.
    '[--ds-input-stroke-extra:transparent]',
    'shadow-[inset_0_0_0_1px_var(--ds-input-stroke-extra)]',

    // The second pixel of the focused stroke, in the same colour as the border showing beneath it.
    // Excluded from invalid, which keeps its red border at 1px as the design draws it, and from
    // open for the same reason the focus border is: the open cells are 1px like every other.
    'group-[[data-focused]:not([data-disabled]):not([data-invalid]):not([data-open])]/ti:[--ds-input-stroke-extra:var(--ds-input-border-caret)]',
  ],
  {
    variants: {
      /**
       * Figma's `Position` axis, which the set now draws in full — all four values have cells, so
       * `middle` is no longer the extrapolation the earlier set left it as. See `TextInputPosition`.
       */
      position: {
        standalone: 'rounded-[16px]',
        first: 'rounded-t-[16px] rounded-b-none',
        middle: 'rounded-none',
        last: 'rounded-b-[16px] rounded-t-none',
      },
      /**
       * The bottom inset, which moves between the box and the control depending on whether anything
       * is rendered below the value.
       *
       * 7px rather than the design's 8px, and the missing pixel is arithmetic rather than a
       * mistake. Figma pads this box 8px top and bottom and then pins it to a 60px minimum, so on
       * the no-helper cells the 2px of slack is split by its CENTER alignment and the content block
       * lands 1px lower than the padding alone would put it. This recipe pays the top inset from
       * inside the control (see the file header), which cannot see that slack, so the 1px is baked
       * into the control's `pt` instead — and the box's own bottom inset gives it back here. The
       * outcome is the measurement that matters: 60px with no helper, 82px with one, exactly as
       * drawn.
       */
      hasHelper: {
        true: 'pb-[7px]',
        false: 'pb-0',
      },
    },
    defaultVariants: { position: 'standalone', hasHelper: false },
  }
);

/**
 * The floating label.
 *
 * Resting it is centred in the 60px box; shrunk it rises to the 12px line near the top. It shrinks
 * when the field is filled OR focused — so a focused empty field shows the shrunk label above an
 * empty line, ready to type.
 *
 * Shrinking on focus used to be a documented divergence from the file, agreed with the requester
 * against a Focus/Empty cell that kept the label at 14px on the value line — which would have left
 * the caret underneath the label text. The reworked set now AGREES: its `Active` cells, the ones
 * that draw the caret, shrink the label to 12px while the field is still empty. The divergence is
 * therefore closed rather than merely tolerated, and the test that guarded it says so.
 *
 * Deliberately NOT `pointer-events-none`, which is the reflex for an overlaid float label. This is a
 * real `<label>` wired to the control by `Field`, so a click on it focuses the input natively;
 * making it click-through would instead drop clicks on the part of it that overhangs the input.
 */
export const textInputLabelRecipe = cva(
  [
    'absolute top-0 left-[24px] origin-left cursor-text select-none',
    'font-sans text-body-md text-[var(--ds-input-label)]',
    'max-w-[calc(100%_-_48px)] truncate',
    // 19px from the padding edge puts the 20px line box at y=20 in the 60px field, which is where
    // the design centres the resting label (447:35126, Label text y=20); 9px puts it on the shrunk
    // line at y=10. The design places the resting label at the box's centre rather than on the value
    // line, so the two are 10px apart.
    'translate-y-[19px] scale-100',
    /**
     * Rise and shrink are ONE composited animation, and both halves of that are deliberate.
     *
     * The properties named here are `translate` and `scale`, not `transform`. Tailwind v4's
     * `translate-*` and `scale-*` utilities set the standalone CSS properties of those names, so a
     * transition on `transform` matches nothing at all — which is what made the label jump to its
     * shrunk position instantly and only then animate its size.
     *
     * The size change is a `scale` rather than a `font-size` for the same reason it is on the same
     * property as the movement: `font-size` relayouts the text on every frame on the main thread,
     * while `translate`/`scale` are composited, so the two could never stay in step. Scaling also
     * fixes the truncation point, which under a `font-size` animation moved mid-flight and made the
     * ellipsis jitter on a long label.
     *
     * `scale-100` is on the base rather than left implicit: interpolating from `scale: none` is not
     * reliably animatable, so the resting state has to say 1 out loud.
     */
    'transition-[translate,scale,color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    /**
     * The shrunk size expressed as a RATIO of the resting one, since the font-size itself does not
     * change — see the note above on why this is a scale and not a font-size.
     *
     * `0.857143` is 12/14: `text-body-md` at rest, landing on `text-body-sm`'s 12px shrunk, which is
     * what the reworked set draws (Label text fs=12 on every isFilled=Yes cell). It used to be
     * 11/14, from a set that drew the floated label at the `label-sm` size — so this is a real
     * change and not a rounding correction. Both states still keep the BODY weight rather than the
     * label style's, because one token feeds both through this scale and the weight cannot be chosen
     * per state; the library draws the filled state at the body weight, so that is the one to keep.
     *
     * The vertical centre is unaffected by the scale. `origin-left` is `left center`, so a 20px line
     * box scaled about its own centre keeps its text centred on y=20 — the same place a real 12px/
     * 20px line box at y=10..30 puts it. The shrink is therefore exact rather than approximate.
     */
    'group-data-[filled]/ti:translate-y-[9px] group-data-[filled]/ti:scale-[0.857143]',
    'group-data-[focused]/ti:translate-y-[9px] group-data-[focused]/ti:scale-[0.857143]',
    /**
     * The ENGAGED step, which only an open `Select` reaches — its `isOpen=Yes` cells take
     * `foreground/secondary/active` on the label, floated or not, so one unconditional rule covers
     * both. Fenced against disabled so the rule below still wins on a disabled field.
     */
    'group-[[data-open]:not([data-disabled])]/ti:text-[var(--ds-input-label-active)]',
    'group-data-[disabled]/ti:cursor-not-allowed group-data-[disabled]/ti:text-[var(--ds-input-label-disabled)]',
  ],
  {
    variants: {
      /**
       * Kept as a NO-OP so both call sites can go on passing it, and so this note has somewhere to
       * live. It used to translate the resting label 22px clear of the leading glyph.
       *
       * It is not needed any more, and the reason is worth recording because deleting a horizontal
       * offset looks like losing a detail. The glyph is now hidden while the field is at rest (see
       * `textInputAffixRecipe`), so at rest there is nothing on the value line for the label to
       * clear — and once the field is filled or focused the label has already risen to its own line
       * ABOVE the glyph, where it never needed an offset. The two states that used to collide can no
       * longer coexist.
       *
       * That also matches the file more closely than the offset did: every unfilled cell draws the
       * label at x=25, with or without a leading glyph configured.
       */
      hasLeftIcon: { true: '', false: '' },
    },
    defaultVariants: { hasLeftIcon: false },
  }
);

/**
 * Shared reset for both shapes.
 *
 * Preflight is deliberately absent from the token layer, so the browser's own input styling is still
 * in play and every reset the field needs is spelled out rather than assumed.
 */
const controlReset = [
  'm-0 block w-full appearance-none border-0 bg-transparent',
  'font-sans',
  'outline-none',
  // Edge and Chrome add their own reveal/clear affordances to password and search inputs, which
  // would sit beside a caller's trailing button.
  '[&::-ms-reveal]:hidden [&::-ms-clear]:hidden',
  '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
];

/**
 * The asterisk after a required field's label.
 *
 * Not in the Figma set, which has no required marker; added so the refreshed field says "required"
 * the way the legacy MUI field always has (`MuiInputLabel`'s asterisk, `palette.error.main`). It
 * takes the destructive role through this field's own invalid slot, and the label's disabled
 * colour on a disabled field. It sits inside the label, so it rises and shrinks with it.
 */
export const textInputRequiredMarkRecipe = cva([
  'text-[var(--ds-input-helper-invalid)]',
  'group-data-[disabled]/ti:text-[var(--ds-input-label-disabled)]',
]);

/**
 * The three colours a control draws, spelled out once per shape.
 *
 * Spelled out rather than parameterised because a Tailwind arbitrary value is a LITERAL STRING: it
 * cannot interpolate a namespace, so one array entry cannot name both families and no variant or
 * factory can produce them. `DatePicker.recipe` and `TextArea.recipe` made the same call for the
 * same reason, and their tests assert that neither reaches into another component's family.
 *
 * The reset above stays shared, because none of it names a slot and so none of it can leak a value
 * between the two shapes.
 */
const inputControlColours = [
  'text-[var(--ds-input-value)]',
  /**
   * The placeholder is HIDDEN until the field is focused — `datePickerControlRecipe`'s rule, for
   * its reason. The set draws a placeholder only from `State=Active`, where the label has already
   * shrunk; its resting empty cell is a 14px label on the value line and nothing else. A visible
   * placeholder at rest shares that line with the label and prints over it, which is what the
   * wizard's "Type something" fields did. Hiding it rather than shrinking the label keeps the
   * resting label the drawing shows, and keeps this field's label rules identical to the date and
   * time fields' — `DatePicker.test.tsx` asserts that pair to the class.
   */
  'placeholder:text-transparent',
  'group-data-[focused]/ti:placeholder:text-[var(--ds-input-placeholder)]',
  // On an open Select the engaged fill (bg-active) drops foreground/secondary to 3.40:1 light /
  // 3.99:1 dark — below AA. The active step restores compliance, fenced against disabled so a
  // disabled open Select keeps its disabled colour.
  'group-[[data-open]:not([data-disabled])]/ti:placeholder:text-[var(--ds-input-placeholder-active)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-input-value-disabled)]',
];

/**
 * The pill's control colours.
 *
 * ── The disabled PLACEHOLDER needs its own rule, and only on this shape ───────────────────────
 *
 * `disabled:text-*` is not enough here, and the reason is the difference between the two shapes
 * rather than anything about the cascade. `::placeholder` takes `color` by inheritance, so on the
 * 60px field `disabled:text-*` reaches it for free — but that field never renders a placeholder at
 * all (it has a floating label), so the question is academic there. The pill is the shape whose
 * resting string IS a placeholder, and an explicit `placeholder:text-*` beats the inherited
 * `color`. Without a disabled counterpart, the one piece of text a disabled empty pill shows stays
 * at full strength.
 *
 * That was survivable while the pill's disabled FILL was a visibly different colour from its
 * resting one. It is not survivable now: both fills resolve to `neutral/100` in light, so the fill
 * step is gone, and the two border colours sit 1.32:1 apart — close to invisible. Measured on a
 * disabled empty pill in light before this rule, the only signals left were a border step nobody
 * can see and a placeholder at 7.68:1, which reads as perfectly active.
 *
 * So the rule is added to the pill and NOT to `inputControlColours`, where it would be dead code.
 * The underlying problem — that `background/control/default` and `-disabled` are the same primitive
 * in light — is a library gap recorded in the PR and the gaps file; this makes the component legible
 * regardless of how that is resolved.
 */
const pillControlColours = [
  'text-[var(--ds-input-pill-value)]',
  'placeholder:text-[var(--ds-input-pill-placeholder)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-input-pill-value-disabled)]',
  'disabled:placeholder:text-[var(--ds-input-pill-value-disabled)]',
];

/**
 * The control for the 60px variant, which is also the field's entire hit area.
 *
 * `pt-[29px]` puts the value's 20px line box at y=30 in the field, which is where the design draws
 * it (447:35126, `.Input` y=30) — and, because the padding is inside the input, everything above
 * that line is still part of the input rather than dead space on the box.
 *
 * The 29 carries Figma's min-height slack as well as its 8px padding; `textInputBoxRecipe`'s
 * `hasHelper` note has the arithmetic and why the box gives the pixel back.
 *
 * Both text sizes in this component are BELOW the 16px at which iOS Safari stops zooming the
 * viewport on focus, so a tap will zoom the page on an iPhone. Reproduced as specified (Figma
 * `Body/md`) rather than silently bumped — flagged on HNS-322 for design.
 *
 * Every type style in this file now names a scale utility, and a utility carries its line height as
 * well as its size — which is why the shared reset no longer sets one. It used to, because the pill
 * drew at a size the scale does not contain and so had no utility to inherit a line box from.
 */
export const textInputControlRecipe = cva(
  [...controlReset, ...inputControlColours, 'text-body-md', PHONE_INPUT_SIZE, 'pt-[29px]', 'cursor-text'],
  {
    variants: {
      /** When helper text follows, the bottom inset moves to the box — see `textInputBoxRecipe`. */
      hasHelper: {
        true: 'pb-0',
        false: 'pb-[9px]',
      },
      /** 24px gutter, plus 16px of icon and the row's 6px gap when the leading slot is filled. */
      hasLeftIcon: { true: 'pl-[46px]', false: 'pl-[24px]' },
      /**
       * 24px gutter, plus a 24px trailing cell and the 4px gap the design puts between it and the
       * value block (447:35126: the block ends at x=247, the cell starts at x=251).
       *
       * The cell is 24px rather than the 16px it used to be because the trailing affordance is now
       * drawn as a Button — `Size=ExtraSmall, Type=Muted, iconOnly=Yes`, a 16px glyph in a 24px
       * round cell. `textInputTrailingButtonRecipe` positions that shared Button separately from
       * the 16px affix recipe.
       */
      hasRightIcon: { true: 'pr-[52px]', false: 'pr-[24px]' },
    },
    defaultVariants: { hasHelper: false, hasLeftIcon: false, hasRightIcon: false },
  }
);

/**
 * An icon slot on the 60px variant.
 *
 * Positioned absolutely so the control underneath can span the full box. The two sides sit at
 * DIFFERENT heights, which is authored rather than accidental: the leading icon is centred on the
 * value line (447:35126, `InputIcon` y=32) while the trailing one is centred in the field.
 *
 * These stay pointer-interactive, since the trailing slot's main job is a control such as a
 * reveal-password toggle. The cost is that a purely decorative icon swallows the cell's click
 * instead of focusing the field.
 *
 * ── The trailing cell has two sizes, and `TextInput` no longer takes the smaller one ───────────
 *
 * 16px in both slots and in both components. The trailing affordance is no longer drawn here at
 * all — `TextInput` renders a real `IconButton` positioned by `textInputTrailingButtonRecipe`, so
 * the 24px cell this recipe briefly carried is gone and `Select`'s 16px caret is the only trailing
 * user left.
 *
 * ── The leading glyph is REVEALED, not always shown ───────────────────────────────────────────
 *
 * This is read off the file's structure rather than off a cell. The leading glyph lives inside the
 * `.Input` row in Figma (`InputIcon` in 447:35126, `Icon` in Select's), and that row exists only on
 * the `isFilled=Yes` variants — so the library draws NO field with a visible leading glyph and an
 * unshrunk label. The glyph and the shrunk label arrive together.
 *
 * Reproducing that means hiding it while the field is at rest and bringing it in with the label's
 * rise, on the same 150ms composited `opacity`/`scale` transition the label uses, for the same
 * reason: `opacity` and `scale` are composited, so the glyph and the label cannot fall out of step
 * the way a `width` or a `font-size` animation would.
 *
 * `pointer-events-none` while hidden is load-bearing rather than tidy. The slot sits on top of the
 * control, so an invisible glyph left interactive would swallow a click on the one part of an empty
 * field a user is most likely to aim at — the left end of the value line.
 *
 * `labelHidden` is exempt from the reveal, and that is the whole reason it participates. `Select`
 * with a hidden label centres its value in the field and has no label to rise, so there is nothing
 * for the glyph to arrive WITH; it is simply always there.
 *
 * `labelHidden` exists because "the value line" MOVES. `Select` shrinks its label to nothing and
 * centres the value in the field instead (`py-[19px]` in `selectTriggerRecipe`), which drops the line
 * 9px — so a leading glyph pinned to the labelled position sat visibly below the text it names. The
 * fork is emitted from `compoundVariants` because only the LEFT slot depends on it: the trailing slot
 * is centred in the field either way, which is exactly why the caret looked right while the glyph did
 * not. `TextInput` has no such prop, so the `false` default keeps it where it was.
 */
export const textInputAffixRecipe = cva(
  [
    'absolute inline-flex shrink-0 items-center justify-center',
    'text-[var(--ds-input-label)]',
    'group-data-[disabled]/ti:text-[var(--ds-input-label-disabled)]',
    /**
     * `size-full` alone does NOT hold this slot to 16px, and the reason is the layer rule this file's
     * neighbours already document. Every glyph in `icons/` is a MUI `SvgIcon` sizing itself at `1em`
     * through UNLAYERED Emotion, while `size-full` is a Tailwind utility in `@layer utilities` — and
     * unlayered author styles beat every layer whatever the specificity. So a `fontSize="small"` call
     * site drew 20px, an unsized one 24px, both overflowing the 24px gutter the value text is padded
     * to. Same defect PR #1130 found in `buttonIconRecipe`.
     *
     * Importance is resolved BEFORE origin and layer, so `!` is the one thing that wins here;
     * out-specifying cannot. That it also outranks a caller's own `fontSize` is the intent, not a
     * side effect — the glyph is 16px on every cell the design draws, either side and at both cell
     * sizes, so there is nothing to negotiate. A caller wanting a different size wants a different
     * slot.
     *
     * Note this is the GLYPH. The cell is the same 16px, so the two agree — but stating the glyph
     * size separately is what keeps an oversized `SvgIcon` from overflowing a cell that cannot grow.
     *
     * Tailwind v4 spells importance as a SUFFIX. The v3 `!size-[16px]` form compiles to a class with
     * no rule behind it, which would look like a no-op fix.
     */
    '[&_svg]:size-[16px]!',
  ],
  {
    variants: {
      /**
       * The vertical position depends on BOTH `side` and `labelHidden`, so every `top-*` is emitted
       * from `compoundVariants` and nowhere else — two competing `top-*` utilities on one element
       * would be resolved by stylesheet order rather than by the order they are listed in, since
       * `cva` composes with `clsx` and does not merge Tailwind conflicts.
       */
      side: {
        left: 'left-[24px]',
        right: 'right-[24px] top-[21px]',
      },
      /** No classes of its own: see `compoundVariants`. */
      labelHidden: { true: '', false: '' },
    },
    compoundVariants: [
      /**
       * Centred on the value line a shrunk label leaves behind — 29px, plus half of a 20px line —
       * and revealed with that label rather than sitting on a line the user cannot see yet.
       */
      {
        side: 'left',
        labelHidden: false,
        class: [
          'top-[31px]',
          'scale-90 opacity-0 pointer-events-none',
          'transition-[opacity,scale] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
          'group-data-[filled]/ti:scale-100 group-data-[filled]/ti:opacity-100 group-data-[filled]/ti:pointer-events-auto',
          'group-data-[focused]/ti:scale-100 group-data-[focused]/ti:opacity-100 group-data-[focused]/ti:pointer-events-auto',
        ].join(' '),
      },
      /**
       * Nothing above the value, so the glyph is centred in the field — and always visible, because
       * there is no label rise for it to arrive with. See the note above.
       */
      { side: 'left', labelHidden: true, class: 'top-[21px]' },
    ],
    defaultVariants: { side: 'right', labelHidden: false },
  }
);

/**
 * Where the trailing BUTTON sits. Position only — no size, no colour, no radius.
 *
 * All three of those come from the `IconButton` this is applied to, which is the point: the design
 * draws the trailing affordance as an instance of `Button` (`Size=ExtraSmall, Type=Tertiary,
 * iconOnly=Yes`), so reproducing it means rendering that component rather than approximating its
 * resting cell with a styled `<span>`. A span cannot express the hover and pressed cells at all.
 *
 * `IconButton size="2xs"` is the shared Button component's 24px ExtraSmall cell. The re-ramped
 * Button set now owns the exact size, icon scale and Tertiary/Default treatment this field needs, so
 * this recipe adds position only rather than duplicating any button styling.
 *
 * `top-[17px]` centres the 24px cell on y=30, the centre of the 40px value block, which is where
 * Figma places it (`Button` y=18, plus the box's 1px border). That puts it on the same line as
 * `Select`'s 16px caret at `top-[21px]` — the two fields' trailing affordances agree in a stacked
 * group even though their cells differ in size.
 *
 * Still under the 44px touch target the product's own accessibility rules ask for, in Figma as much
 * as here. Flagged rather than silently grown: a 44px control in a 60px field would not fit the
 * drawn layout, so it is a design question.
 */
export const textInputTrailingButtonRecipe = cva(['absolute right-[24px] top-[17px]']);

/**
 * Helper / error text. Inside the border for this variant, below it for the pill.
 *
 * The two COLOUR reads sit in the `shape` branches rather than in the base, because each shape reads
 * its own slot family and a Tailwind arbitrary value cannot interpolate a namespace. Only the
 * shape-independent layout is shared.
 */
export const textInputHelperRecipe = cva(
  [
    'flex items-center',
    // `m-0` is load-bearing, not tidiness: `Field.Description` renders a `<p>`, and with Preflight
    // deliberately absent from the token layer it keeps the browser's default `margin: 1em 0` — 28px
    // that grew the box past 100px while the `<div>` that `Field.Error` renders stayed correct.
    'm-0',
    'font-sans',
  ],
  {
    variants: {
      shape: {
        // Aligned with the value text, which pays its own 24px gutter from inside the input. The
        // smaller body step rather than the one the design draws, per the requester — it is what the
        // two role-based fields already render, so all five fields agree.
        default: [
          'px-[24px] text-body-sm',
          'text-[var(--ds-input-helper)]',
          /**
           * Mutually exclusive rather than ordered, the same discipline `textInputBoxRecipe` uses
           * and for the same reason: which of two same-property utilities wins is decided by their
           * order in the GENERATED stylesheet, not by their order in this array, so a precedence
           * written as "disabled comes last" is not a precedence at all.
           *
           * A disabled field is never red. The design's Disabled cells wash every string in the box
           * to the one disabled foreground, helper text included, and that holds whether or not the
           * field also carries an error.
           *
           * The open state is fenced against both `data-disabled` (same discipline) and
           * `data-invalid`: a textInputBoxRecipe fence keeps `data-open` and `data-invalid` mutually
           * exclusive on the BOX, so an errored open Select keeps its red helper — the active step
           * is not needed and must not override it.
           */
          'group-[[data-open]:not([data-disabled]):not([data-invalid])]/ti:text-[var(--ds-input-helper-active)]',
          'group-[[data-invalid]:not([data-disabled])]/ti:text-[var(--ds-input-helper-invalid)]',
          'group-data-[disabled]/ti:text-[var(--ds-input-label-disabled)]',
        ],
        /**
         * Indented to the leftmost thing in the pill (node 558:23435). One value covers both cases
         * the requester asked about: a leading icon and a bare placeholder start at the SAME x,
         * because the icon is first in flow and the control follows it — so aligning to the box's
         * content edge aligns to whichever is showing.
         *
         * Figma draws this as a flat 20px because its stroke is inside-aligned and does not displace
         * children. In CSS the border does, so the border width is added back — otherwise the helper
         * sits a pixel left of the content it is meant to line up with.
         */
        pill: [
          'pl-[calc(20px_+_var(--ds-input-pill-border-width))] text-body-sm',
          'text-[var(--ds-input-pill-helper)]',
          'group-data-[invalid]/ti:text-[var(--ds-input-pill-helper-invalid)]',
        ],
        /**
         * The chat composer (`ChatInput`, node 1447:132028). Geometrically the pill's rule — 20px to
         * the box's content edge, plus the border width CSS displaces it by — but reading
         * `--ds-chat-input-*`, because that component writes its own family.
         *
         * A shape of its own rather than reusing `pill`: this recipe is a READER of whichever family
         * the shape names, so a shape and a family are one choice. `ChatInput` shared `pill` until
         * the families were split, at which point its helper silently fell back to inherited colour
         * — the error description stopped going red and nothing but a computed-style assertion could
         * see it. Keeping the shapes and the families in step is what stops that recurring.
         *
         * ⚠️ The INDENT is a recorded disagreement with the reworked chat set, not a match.
         *
         * That set draws its helper at x=0 — flush with the pill's left edge, 21px to the left of
         * the value it describes and under the rounded corner. The indent is kept anyway, for two
         * reasons: it is the natural result of dropping a width-filling text node into the root
         * stack rather than a position anyone chose (the set's own HIDDEN helper copies sit at x=25,
         * which is where someone did place one), and every other field in this family aligns its
         * helper with its value. Raised with design; if x=0 turns out to be deliberate, this is the
         * one line to change and `ChatInput` needs no edit at all.
         */
        chat: [
          'pl-[calc(20px_+_var(--ds-chat-input-border-width))] text-body-sm',
          'text-[var(--ds-chat-input-helper)]',
          'group-data-[invalid]/ti:text-[var(--ds-chat-input-helper-invalid)]',
        ],
      },
    },
    defaultVariants: { shape: 'default' },
  }
);

// ── Pill ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The pill wrapper. Unlike the 60px input, the pill's helper text sits OUTSIDE the border, 4px
 * below it (node 487:38327), so the wrapper — not the bordered box — is what stacks.
 */
export const textInputPillRootRecipe = cva(['group/ti ds-input-pill', 'relative flex w-full flex-col gap-[4px]']);

/**
 * The pill box. Always fully rounded: the pill set has no Corner Radius axis, so there is no
 * `position` variant here.
 *
 * `rounded-[42px]` is authored as a fixed radius equal to the height rather than `rounded-full`, and
 * it is reproduced that way — the two only agree while the pill is exactly 42px tall.
 *
 * Horizontal padding only. The vertical inset is paid by the control, for the hit-area reason in the
 * file header.
 *
 * ── Its state matrix is the 60px field's, which is a change and not a port ────────────────────
 *
 * Every rule below is the same rule as in `textInputBoxRecipe`, against the pill's own slot family.
 *
 * That parity was authored AHEAD of the file. `Text Input_Pill` (487:38319) used to move the border
 * alone on hover, keep `border/focus` rather than the accent, stay 1px in every state and draw a
 * translucent halo; the consequences were priced and full parity was chosen anyway, with
 * `design-system.css` recording what was traded.
 *
 * **The file has since caught up.** That set was rebuilt against `Text Input` (447:35126) and now
 * draws `background/control/*`, `border/control/*` and a 2px `border/accent` Active cell itself, so
 * every rule below matches its own source rather than diverging from it. The traded-off notes in
 * `design-system.css` are history, not open questions.
 *
 * One difference the rebuild did NOT close, because it cannot: the file grows 2px on Active where
 * this recipe does not. Figma adds stroke weight to an auto-layout frame's size even at
 * `strokeAlign: INSIDE`, so the rebuilt set pays 7px of frame padding in its two Active cells to
 * stay at 42px — an unbound value, since there is no `x1,75` spacing token. Recorded as a gap. The
 * inset-shadow approach below has no equivalent cost.
 *
 * The halo is worth knowing about here:
 *
 * **The pill's focus halo is gone, and it was the only one in the family that was DRAWN.** It was an
 * `outline` keyed to `data-focused`, coincident with the box at offset 0 (node 487:38417), and it
 * fired on every focus — pointer or keyboard. Its 2px accent border replaces it, which is why the
 * transition below no longer lists `outline-color` and does list `box-shadow`. The long note on
 * `--ds-input-pill-halo-inner` has the argument; the short version is that a pill ringing on a mouse
 * click beside a text input that does not is exactly the split a shared family exists to prevent.
 */
export const textInputPillBoxRecipe = cva([
  'relative flex w-full items-center gap-[4px] overflow-hidden',
  'px-[20px] rounded-[42px]',
  'border-solid border-[length:var(--ds-input-pill-border-width)] border-[var(--ds-input-pill-border)]',
  'bg-[var(--ds-input-pill-bg)]',
  'cursor-text',
  'transition-[border-color,background-color,box-shadow] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

  // Hover moves the FILL as well as the border, as on the 60px shape. New to the pill — see
  // `--ds-input-pill-bg-hover`. Hover stays excluded from every other state so it cannot fight them.
  'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-input-pill-border-hover)]',
  'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:bg-[var(--ds-input-pill-bg-hover)]',

  'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-input-pill-border-caret)]',
  'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-input-pill-border-invalid)]',
  'group-data-[disabled]/ti:border-[var(--ds-input-pill-border-disabled)]',
  'group-data-[disabled]/ti:bg-[var(--ds-input-pill-bg-disabled)]',
  'group-data-[disabled]/ti:cursor-not-allowed',

  // Off by default, so the declaration below can be unconditional.
  '[--ds-input-pill-stroke-extra:transparent]',
  'shadow-[inset_0_0_0_1px_var(--ds-input-pill-stroke-extra)]',

  /**
   * The second pixel of the focused stroke, painted as an inset shadow rather than by growing the
   * border — the layout argument in `textInputBoxRecipe` applies here with a different victim. The
   * pill has no absolutely positioned label to shift, but its icons sit in FLOW either side of the
   * control, so a border that widened on focus would nudge both glyphs and the value text inward by
   * a pixel every time the field was clicked into.
   *
   * Excluded from invalid, which keeps its red border at 1px as both sets draw it.
   */
  'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-input-pill-stroke-extra:var(--ds-input-pill-border-caret)]',
]);

/**
 * The pill's control. `py-[10px]` over a 20px line is the pill's whole 40px interior, so the input
 * fills it top to bottom and the 42px pill is clickable throughout rather than across a 20px strip.
 */
export const textInputPillControlRecipe = cva([
  ...controlReset,
  ...pillControlColours,
  'min-w-0 flex-1 py-[10px] text-body-md',
  PHONE_INPUT_SIZE,
  'cursor-text',
]);

/**
 * The pill's LEADING glyph slot, kept in flow before the control.
 *
 * Leading only, now that the trailing affordance is a real button — see
 * `textInputPillTrailingButtonRecipe`. It was a two-sided slot while the pill drew a bare glyph on
 * each end, which is why nothing here names a side.
 *
 * `size-[16px]!` on the glyph for the same reason as `textInputAffixRecipe` — see the long note
 * there. In flow rather than absolute, so an oversized glyph was not misaligned vertically here; it
 * just ate the pill's leading padding.
 */
export const textInputIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[var(--ds-input-pill-icon)]',
  'group-data-[disabled]/ti:text-[var(--ds-input-pill-icon-disabled)]',
  '[&_svg]:size-[16px]!',
]);

/**
 * Where the pill's trailing BUTTON sits. Like `textInputTrailingButtonRecipe`, this carries no size,
 * colour or radius — all three come from the `IconButton` it is applied to.
 *
 * Unlike that recipe it carries no POSITION either, and the empty-looking rule is the point. The
 * 60px field overlays its trailing button on a control that pays its own padding, so it needs an
 * absolute offset (`right-[24px] top-[17px]`). The pill's box is a `flex items-center` row that
 * already places its leading glyph in flow, so the button lands correctly by being the last child —
 * and it stays correct when the border width changes, which an absolute offset would not.
 *
 * `shrink-0` is the whole declaration, and it is load-bearing rather than defensive: the control is
 * `flex-1`, so without it a long value compresses the 24px cell into an oval.
 *
 * The 24px cell is under the 44px touch target the product's accessibility rules ask for — the same
 * flag `textInputTrailingButtonRecipe` carries, and the same answer: a 44px control does not fit a
 * 42px pill, so it is a design question rather than something to silently grow.
 */
export const textInputPillTrailingButtonRecipe = cva(['shrink-0']);
